import React, { useState, useEffect, useRef } from 'react';
import {
    List,
    useListContext,
    useDataProvider,
    useRefresh,
    Edit,
    SimpleForm,
    useRecordContext,
    useNotify,
    useRedirect
} from 'react-admin';
import {
    Box,
    Typography,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField as MuiTextField,
    Popover,
    Chip,
    Stack,
    Tabs,
    Tab,
    ButtonBase,
    Skeleton,
    InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SyncIcon from '@mui/icons-material/Sync';
import TelegramIcon from '@mui/icons-material/Telegram';
import SearchIcon from '@mui/icons-material/Search';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import EmojiEmotionsOutlinedIcon from '@mui/icons-material/EmojiEmotionsOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { uploadNewsImage, uploadNewsVideo, deleteNewsMedia } from './s3Service';
import DetailLoadingState from './components/DetailLoadingState';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { eventsApiUrl, newsApiUrl } from './apiConfig';

const NEWS_EDITOR_MODULES = {
    toolbar: [
        [{ header: [2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['blockquote', 'link'],
        ['clean'],
    ],
};

const NEWS_EDITOR_FORMATS = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'blockquote',
    'link',
];

const NEWS_EMOJIS = [
    '😄', '🎉', '🔥', '😍', '👍', '✨', '📢', '📰', '✅', '💡', '📌',
    '🐾', '🐶', '🐺', '🦊', '🐱', '🦁', '🐯', '🐴', '🦓', '🦒', '🐮',
    '🐷', '🐗', '🐵', '🦍', '🐻', '🐨', '🐼', '🦘', '🦥', '🦦', '🦔',
    '🐰', '🐹', '🐭', '🐿️', '🦫', '🦌', '🦬', '🐘', '🦏', '🦛', '🐪',
    '🐫', '🦙', '🦒', '🐧', '🦉', '🦅', '🦜', '🐦', '🦢', '🦩', '🦚',
    '🦆', '🐸', '🐢', '🦎', '🐍', '🐊', '🐙', '🦑', '🦀', '🐠', '🐟',
    '🐬', '🦈', '🐳', '🐋', '🪼', '🐞', '🦋', '🐝', '🐜', '🪲', '🕷️',
];

// Компонент для отображения списка новостей
// Статусы новости на бэкенде.
const STATUS_META = {
    draft: { label: 'На модерации', color: 'warning' },
    published: { label: 'Опубликовано', color: 'success' },
    rejected: { label: 'Отклонено', color: 'default' },
};

const StatusChip = ({ record }) => {
    const meta = STATUS_META[record?.status] || STATUS_META.published;
    return <Chip size="small" label={meta.label} color={meta.color} variant="outlined" />;
};

// Кнопки «Опубликовать»/«Отклонить» для черновиков из телеграма.
// Для опубликованных руками новостей (source=admin) модерация не нужна.
const ModerationButtons = ({ record }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const [busy, setBusy] = useState(false);

    if (!record || record.source !== 'telegram') {
        return null;
    }

    const moderate = async (action, e) => {
        e.stopPropagation(); // клик по строке открывает редактирование — гасим его
        setBusy(true);
        try {
            await dataProvider.setNewsStatus(record.id, action);
            notify(action === 'publish' ? 'Новость опубликована' : 'Новость отклонена', {
                type: 'success',
            });
            refresh();
        } catch (error) {
            notify(`Ошибка: ${error.message}`, { type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Stack direction="row" spacing={1}>
            {record.status !== 'published' && (
                <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    disabled={busy}
                    onClick={(e) => moderate('publish', e)}
                >
                    Опубликовать
                </Button>
            )}
            {record.status !== 'rejected' && (
                <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    startIcon={<CancelIcon />}
                    disabled={busy}
                    onClick={(e) => moderate('reject', e)}
                >
                    Отклонить
                </Button>
            )}
        </Stack>
    );
};

const getNewsImages = (record) => String(record?.image_url || '')
    .split(';')
    .map((url) => url.trim())
    .filter(Boolean);

const getNewsExcerpt = (html) => {
    const text = String(html || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/\s+/g, ' ')
        .trim();
    return text || 'Описание пока не добавлено';
};

const formatNewsDate = (value) => {
    if (!value) return 'Дата не указана';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Дата не указана';
    return date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
};

const SourceChip = ({ source }) => source === 'telegram' ? (
    <Chip className="news-card-source" size="small" icon={<TelegramIcon />} label="Telegram" />
) : (
    <Chip className="news-card-source" size="small" label="Админка" />
);

const NewsCard = ({ record }) => {
    const redirect = useRedirect();
    const images = getNewsImages(record);
    const edit = () => redirect('edit', 'news', record.id);

    return (
        <Paper component="article" className="news-card" elevation={0}>
            <ButtonBase
                className="news-card-main"
                onClick={edit}
                aria-label={`Редактировать новость «${record.title || `№ ${record.id}`}»`}
            >
                <Box className="news-card-media">
                    {images[0] ? (
                        <img src={images[0]} alt="" loading="lazy" />
                    ) : (
                        <Box className="news-card-media-empty">
                            <ArticleOutlinedIcon />
                            <Typography variant="body2">Без обложки</Typography>
                        </Box>
                    )}
                    <Stack className="news-card-badges" direction="row" spacing={0.75}>
                        <StatusChip record={record} />
                        <SourceChip source={record.source} />
                    </Stack>
                    <Chip className="news-card-id" size="small" label={`#${record.id}`} />
                </Box>

                <Box className="news-card-content">
                    <Typography className="news-card-date" variant="caption">
                        {formatNewsDate(record.event_date || record.created_at)}
                    </Typography>
                    <Typography component="h2" className="news-card-title">
                        {record.title || 'Без названия'}
                    </Typography>
                    <Typography className="news-card-excerpt" variant="body2">
                        {getNewsExcerpt(record.description)}
                    </Typography>
                    <Stack className="news-card-media-summary" direction="row" spacing={1.5}>
                        {images.length > 0 && (
                            <span><ImageOutlinedIcon />{images.length} фото</span>
                        )}
                        {record.video_url && (
                            <span><VideocamOutlinedIcon />Видео</span>
                        )}
                        {!images.length && !record.video_url && <span>Без медиа</span>}
                    </Stack>
                </Box>
            </ButtonBase>

            <Box className="news-card-footer">
                <Box className="news-card-moderation">
                    <ModerationButtons record={record} />
                </Box>
                <Button size="small" startIcon={<EditOutlinedIcon />} onClick={edit}>
                    Редактировать
                </Button>
            </Box>
        </Paper>
    );
};

const NewsCards = () => {
    const { data = [], isLoading } = useListContext();

    if (isLoading) {
        return (
            <Box className="news-card-grid" aria-label="Загрузка новостей">
                {[0, 1, 2, 3, 4, 5].map((item) => (
                    <Paper className="news-card news-card-skeleton" elevation={0} key={item}>
                        <Skeleton variant="rectangular" className="news-card-skeleton-media" />
                        <Box className="news-card-content">
                            <Skeleton width="38%" />
                            <Skeleton height={34} />
                            <Skeleton />
                            <Skeleton width="72%" />
                        </Box>
                    </Paper>
                ))}
            </Box>
        );
    }

    if (!data.length) return <EmptyNewsList filtered />;

    return (
        <Box className="news-card-grid">
            {data.map((record) => <NewsCard key={record.id} record={record} />)}
        </Box>
    );
};

// Кнопка ручной проверки телеграм-канала.
// Плановый опрос идёт на бэкенде сам; кнопка нужна, чтобы не ждать интервала.
const SyncTelegramButton = () => {
    const notify = useNotify();
    const refresh = useRefresh();
    const [busy, setBusy] = useState(false);
    const [resultText, setResultText] = useState('');

    const handleSync = async () => {
        setBusy(true);
        setResultText('Проверяем канал…');
        try {
            const token = sessionStorage.getItem('authToken');
            if (!token) {
                throw new Error('Сессия истекла — войдите в админку заново');
            }

            const response = await fetch(
                `${newsApiUrl}/telegram/sync`,
                {
                    method: 'POST',
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const body = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(body.detail || `Ошибка API (${response.status})`);
            }

            const data = body;
            if (data.imported > 0) {
                const message = `Импортировано новостей: ${data.imported}`;
                setResultText(message);
                notify(message, { type: 'success' });
            } else {
                const message = `Проверено постов: ${data.checked}. Новых нет`;
                setResultText(message);
                notify(message, { type: 'info' });
            }
            refresh();
        } catch (error) {
            const message = `Не удалось проверить канал: ${error.message}`;
            setResultText(message);
            notify(message, { type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Button
                variant="outlined"
                startIcon={<SyncIcon />}
                onClick={handleSync}
                disabled={busy}
            >
                {busy ? 'Проверяем…' : 'Проверить Telegram'}
            </Button>
            {resultText && (
                <Typography
                    variant="caption"
                    color={resultText.startsWith('Не удалось') ? 'error' : 'text.secondary'}
                    sx={{ maxWidth: 260 }}
                >
                    {resultText}
                </Typography>
            )}
        </Box>
    );
};

const getNewsCountLabel = (count) => {
    const mod10 = count % 10;
    const mod100 = count % 100;
    if (mod10 === 1 && mod100 !== 11) return `${count} новость`;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} новости`;
    return `${count} новостей`;
};

// Шапка каталога: основные действия и понятный контекст вместо технического тулбара.
const NewsListHeader = () => {
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const { total = 0, filterValues, setFilters, displayedFilters } = useListContext();

    const handleSearch = (event) => {
        const q = event.target.value;
        const next = { ...filterValues };
        if (q) next.q = q;
        else delete next.q;
        setFilters(next, displayedFilters, true);
    };

    return (
        <>
            <Box className="news-list-header">
                <Box className="news-list-heading">
                    <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap">
                        <Typography component="h1">Новости</Typography>
                        <Chip size="small" label={getNewsCountLabel(total)} />
                    </Stack>
                    <Typography>Публикации для сайта и мобильного приложения зоопарка</Typography>
                </Box>
                <Box className="news-list-actions">
                    <SyncTelegramButton />
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateDialogOpen(true)}
                    >
                        Создать новость
                    </Button>
                </Box>
            </Box>
            <Paper className="news-list-filters" elevation={0}>
                <MuiTextField
                    className="news-list-search"
                    value={filterValues.q || ''}
                    onChange={handleSearch}
                    placeholder="Найти по заголовку, тексту или ID"
                    label="Поиск новостей"
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start"><SearchIcon /></InputAdornment>
                        ),
                    }}
                />
                <NewsStatusTabs />
            </Paper>
            <CreateNewsDialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
            />
        </>
    );
};

// Вкладки-фильтры по статусу. 'all' убирает фильтр status из запроса.
const STATUS_TABS = [
    { value: 'all', label: 'Все' },
    { value: 'draft', label: 'На модерации' },
    { value: 'published', label: 'Опубликованные' },
    { value: 'rejected', label: 'Отклонённые' },
];

const NewsStatusTabs = () => {
    const { filterValues, setFilters, displayedFilters } = useListContext();
    const current = filterValues.status || 'all';

    const handleChange = (_e, value) => {
        const next = { ...filterValues };
        if (value === 'all') {
            delete next.status;
        } else {
            next.status = value;
        }
        setFilters(next, displayedFilters);
    };

    return (
        <Box className="news-status-tabs">
            <Tabs value={current} onChange={handleChange} variant="scrollable" scrollButtons="auto">
                {STATUS_TABS.map((tab) => (
                    <Tab key={tab.value} value={tab.value} label={tab.label} />
                ))}
            </Tabs>
        </Box>
    );
};

// Диалог создания новости
const CreateNewsDialog = ({ open, onClose }) => {
    const [title, setTitle] = useState('');
    const [creating, setCreating] = useState(false);
    
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const redirect = useRedirect();

    const handleCreate = async () => {
        if (!title.trim()) {
            notify('Название обязательно', { type: 'warning' });
            return;
        }

        setCreating(true);

        try {
            // Получаем логин авторизованного пользователя
            const username = localStorage.getItem('username') || '';
            
            // Текущая дата и время
            const now = new Date().toISOString();

            const result = await dataProvider.create('news', {
                data: {
                    event_id: 0,
                    employee_id: username,
                    event_date: now,
                    title: title.trim(),
                    description: '',
                    image_url: '',
                    video_url: ''
                }
            });
            
            notify('Новость успешно создана', { type: 'success' });
            handleClose();
            redirect('edit', 'news', result.data.id);
        } catch (error) {
            console.error('Error creating news:', error);
            notify(`Ошибка при создании: ${error.message}`, { type: 'error' });
        } finally {
            setCreating(false);
        }
    };

    const handleClose = () => {
        setTitle('');
        onClose();
    };

    return (
        <Dialog className="news-create-dialog" open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box className="news-create-dialog-icon"><ArticleOutlinedIcon /></Box>
                <Box>
                    <Typography variant="overline">Новая публикация</Typography>
                    <Typography component="h2">Создать новость</Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Typography className="news-create-dialog-copy" variant="body2">
                    Сначала задайте рабочий заголовок. После создания откроется полноценный редактор с текстом, фотографиями и видео.
                </Typography>
                <MuiTextField
                    label="Заголовок новости"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && title.trim() && !creating) handleCreate();
                    }}
                    fullWidth
                    required
                    autoFocus
                    placeholder="Например: У белых медведей пополнение"
                    helperText={`${title.length} символов · заголовок можно изменить позже`}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} variant="outlined">Отмена</Button>
                <Button 
                    onClick={handleCreate} 
                    variant="contained" 
                    startIcon={<AddIcon />}
                    disabled={!title.trim() || creating}
                >
                    {creating ? 'Создаём…' : 'Создать и продолжить'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Пустой компонент для отображения когда нет данных
const EmptyNewsList = ({ filtered = false }) => {
    const [createDialogOpen, setCreateDialogOpen] = useState(false);

    return (
        <>
            <Paper className="news-list-empty" elevation={0}>
                <Box className="news-list-empty-icon"><ArticleOutlinedIcon /></Box>
                <Typography variant="h6">
                    {filtered ? 'Ничего не найдено' : 'Новостей пока нет'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {filtered
                        ? 'Попробуйте изменить запрос или выбрать другой статус.'
                        : 'Создайте первую публикацию — она появится в этом каталоге.'}
                </Typography>
                {!filtered && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateDialogOpen(true)}
                    >
                        Создать новость
                    </Button>
                )}
            </Paper>
            <CreateNewsDialog 
                open={createDialogOpen} 
                onClose={() => setCreateDialogOpen(false)} 
            />
        </>
    );
};

// Компонент списка новостей
export const NewsList = (props) => (
    <List 
        {...props}
        exporter={false}
        title="Новости"
        perPage={12}
        sort={{ field: 'id', order: 'DESC' }}
        actions={false}
        empty={<EmptyNewsList />}
        className="news-list-page"
    >
        <NewsListHeader />
        <NewsCards />
    </List>
);

// Контент формы редактирования
const NewsEditContent = () => {
    const record = useRecordContext();
    const [eventId, setEventId] = useState(0);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [imageUrls, setImageUrls] = useState([]); // Массив URL изображений
    const [videoUrl, setVideoUrl] = useState('');
    const [updating, setUpdating] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [events, setEvents] = useState([]);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [eventSearchText, setEventSearchText] = useState('');
    const [showEventDropdown, setShowEventDropdown] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
    const quillRef = useRef(null);
    
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const redirect = useRedirect();

    // Загрузка списка событий
    useEffect(() => {
        const loadEvents = async () => {
            setEventsLoading(true);
            try {
                const response = await fetch(eventsApiUrl);
                const data = await response.json();
                setEvents(data);
            } catch (error) {
                console.error('Error loading events:', error);
                notify('Ошибка загрузки событий', { type: 'error' });
            } finally {
                setEventsLoading(false);
            }
        };
        loadEvents();
    }, [notify]);

    // Закрытие выпадающего списка при клике вне его
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showEventDropdown && !event.target.closest('.event-autocomplete')) {
                setShowEventDropdown(false);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showEventDropdown]);

    useEffect(() => {
        if (record) {
            setEventId(record.event_id || 0);
            setTitle(record.title || '');
            setDescription(record.description || '');
            
            // Парсим image_url в массив, разделяя по ;
            const imageUrlString = record.image_url || '';
            const imageUrlsArray = imageUrlString ? imageUrlString.split(';').filter(url => url.trim()) : [];
            setImageUrls(imageUrlsArray);
            
            setVideoUrl(record.video_url || '');
            
            // Найти название события по ID
            if (record.event_id && record.event_id !== 0) {
                const event = events.find(e => e.id === record.event_id);
                if (event) {
                    setEventSearchText(event.name);
                }
            } else {
                setEventSearchText('');
            }
        }
    }, [record, events]);

    const handleSave = async () => {
        if (!title) {
            notify('Название обязательно', { type: 'warning' });
            return;
        }

        setUpdating(true);

        try {
            // Объединяем массив URL изображений в строку через ;
            const imageUrlString = imageUrls.join(';');
            
            await dataProvider.update('news', {
                id: record.id,
                data: {
                    event_id: eventId,
                    employee_id: record.employee_id, // Отправляем оригинальное значение из записи
                    event_date: record.event_date, // Отправляем оригинальное значение из записи
                    title: title,
                    description: description || '',
                    image_url: imageUrlString,
                    video_url: videoUrl || ''
                },
                previousData: record
            });
            
            notify('Новость успешно обновлена', { type: 'success' });
            redirect('list', 'news');
        } catch (error) {
            console.error('Error updating news:', error);
            notify(`Ошибка при сохранении: ${error.message}`, { type: 'error' });
        } finally {
            setUpdating(false);
        }
    };

    const handleDelete = async () => {
        try {
            await dataProvider.delete('news', {
                id: record.id,
                previousData: record
            });

            notify('Новость успешно удалена', { type: 'success' });
            setDeleteDialogOpen(false);
            redirect('list', 'news');
        } catch (error) {
            console.error('Error deleting news:', error);
            notify(`Ошибка при удалении: ${error.message}`, { type: 'error' });
            setDeleteDialogOpen(false);
        }
    };

    // Сохранить текущие правки и опубликовать черновик из телеграма одним действием.
    const handlePublish = async () => {
        setUpdating(true);
        try {
            await dataProvider.update('news', {
                id: record.id,
                data: {
                    event_id: eventId,
                    employee_id: record.employee_id,
                    event_date: record.event_date,
                    title: title,
                    description: description || '',
                    image_url: imageUrls.join(';'),
                    video_url: videoUrl || ''
                },
                previousData: record
            });
            await dataProvider.setNewsStatus(record.id, 'publish');

            notify('Новость опубликована', { type: 'success' });
            redirect('list', 'news');
        } catch (error) {
            console.error('Error publishing news:', error);
            notify(`Ошибка при публикации: ${error.message}`, { type: 'error' });
        } finally {
            setUpdating(false);
        }
    };

    // Функция для извлечения имени файла из URL
    const extractFileNameFromUrl = (url) => {
        if (!url) return null;
        try {
            const urlObj = new URL(url);
            const pathParts = urlObj.pathname.split('/');
            return pathParts[pathParts.length - 1]; // Последняя часть пути - имя файла
        } catch (error) {
            console.error('Error extracting filename from URL:', error);
            return null;
        }
    };

    // Обработка загрузки изображений (множественная)
    const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        if (!record || !record.id) {
            notify('Ошибка: ID новости не найден', { type: 'error' });
            return;
        }

        setUploading(true);

        try {
            const uploadPromises = files.map(async (file) => {
                // Используем название новости как префикс
                const prefix = title.replace(/[^a-zA-Zа-яА-Я0-9]/g, '_') || `news_${record.id}`;
                return await uploadNewsImage(file, prefix);
            });

            const uploadedUrls = await Promise.all(uploadPromises);
            
            // Добавляем новые URL к существующим
            const newImageUrls = [...imageUrls, ...uploadedUrls];
            setImageUrls(newImageUrls);
            
            // Автоматически сохраняем новость с новыми URL изображений
            const imageUrlString = newImageUrls.join(';');
            await dataProvider.update('news', {
                id: record.id,
                data: {
                    event_id: eventId,
                    employee_id: record.employee_id,
                    event_date: record.event_date,
                    title: title,
                    description: description || '',
                    image_url: imageUrlString,
                    video_url: videoUrl || ''
                },
                previousData: record
            });
            
            notify(`Загружено ${files.length} изображений`, { type: 'success' });
        } catch (error) {
            console.error('Error uploading images:', error);
            notify(`Ошибка загрузки изображений: ${error.message}`, { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    // Обработка загрузки видео
    const handleVideoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!record || !record.id) {
            notify('Ошибка: ID новости не найден', { type: 'error' });
            return;
        }

        setUploading(true);

        try {
            // Используем название новости как префикс
            const prefix = title.replace(/[^a-zA-Zа-яА-Я0-9]/g, '_') || `news_${record.id}`;
            const url = await uploadNewsVideo(file, prefix);
            
            setVideoUrl(url);
            
            // Автоматически сохраняем новость с новым URL видео
            const imageUrlString = imageUrls.join(';');
            await dataProvider.update('news', {
                id: record.id,
                data: {
                    event_id: eventId,
                    employee_id: record.employee_id,
                    event_date: record.event_date,
                    title: title,
                    description: description || '',
                    image_url: imageUrlString,
                    video_url: url
                },
                previousData: record
            });
            
            notify('Видео успешно загружено', { type: 'success' });
        } catch (error) {
            console.error('Error uploading video:', error);
            notify(`Ошибка загрузки видео: ${error.message}`, { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    // Обработка удаления конкретного изображения
    const handleDeleteImage = async (index) => {
        if (index < 0 || index >= imageUrls.length) return;

        setUploading(true);

        try {
            const imageUrl = imageUrls[index];
            const fileName = extractFileNameFromUrl(imageUrl);
            if (fileName) {
                await deleteNewsMedia(fileName);
            }
            
            // Удаляем URL из массива
            const newImageUrls = imageUrls.filter((_, i) => i !== index);
            setImageUrls(newImageUrls);
            
            // Автоматически сохраняем новость с обновленным списком изображений
            const imageUrlString = newImageUrls.join(';');
            await dataProvider.update('news', {
                id: record.id,
                data: {
                    event_id: eventId,
                    employee_id: record.employee_id,
                    event_date: record.event_date,
                    title: title,
                    description: description || '',
                    image_url: imageUrlString,
                    video_url: videoUrl || ''
                },
                previousData: record
            });
            
            notify('Изображение удалено', { type: 'success' });
        } catch (error) {
            console.error('Error deleting image:', error);
            notify(`Ошибка удаления изображения: ${error.message}`, { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    // Обработка удаления видео
    const handleDeleteVideo = async () => {
        if (!videoUrl) return;

        setUploading(true);

        try {
            const fileName = extractFileNameFromUrl(videoUrl);
            if (fileName) {
                await deleteNewsMedia(fileName);
            }
            
            setVideoUrl('');
            
            // Автоматически сохраняем новость без URL видео
            const imageUrlString = imageUrls.join(';');
            await dataProvider.update('news', {
                id: record.id,
                data: {
                    event_id: eventId,
                    employee_id: record.employee_id,
                    event_date: record.event_date,
                    title: title,
                    description: description || '',
                    image_url: imageUrlString,
                    video_url: ''
                },
                previousData: record
            });
            
            notify('Видео удалено', { type: 'success' });
        } catch (error) {
            console.error('Error deleting video:', error);
            notify(`Ошибка удаления видео: ${error.message}`, { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    // Форматирование даты в локальный часовой пояс пользователя
    const formatLocalDateTime = (dateString) => {
        if (!dateString) return '';
        try {
            // Если дата не содержит информацию о часовом поясе, добавляем 'Z' для UTC
            let isoString = dateString;
            if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('T')) {
                // Формат: YYYY-MM-DD HH:MM:SS
                isoString = dateString.replace(' ', 'T') + 'Z';
            } else if (dateString.includes('T') && !dateString.endsWith('Z') && !dateString.includes('+')) {
                // Формат: YYYY-MM-DDTHH:MM:SS (без Z)
                isoString = dateString + 'Z';
            }
            
            const date = new Date(isoString);
            if (isNaN(date.getTime())) return '';
            
            // Используем локальный часовой пояс пользователя (браузер определяет автоматически)
            return date.toLocaleString('ru-RU', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            });
        } catch (error) {
            console.error('Error formatting date:', error);
            return '';
        }
    };

    // Фильтрация событий по поисковому запросу
    const filteredEvents = events.filter(event => 
        event.name.toLowerCase().includes(eventSearchText.toLowerCase())
    );

    const handleEventSelect = (event) => {
        setEventId(event.id);
        setEventSearchText(event.name);
        setShowEventDropdown(false);
    };

    const handleEventSearchChange = (e) => {
        setEventSearchText(e.target.value);
        setShowEventDropdown(true);
        if (!e.target.value) {
            setEventId(0);
        }
    };

    const handleOpenEmojiPicker = (event) => {
        setEmojiAnchorEl(event.currentTarget);
    };

    const handleCloseEmojiPicker = () => {
        setEmojiAnchorEl(null);
    };

    const handleInsertEmoji = (emoji) => {
        const editor = quillRef.current?.getEditor?.();
        if (editor) {
            const range = editor.getSelection(true);
            const insertAt = range ? range.index : editor.getLength();
            editor.insertText(insertAt, `${emoji} `, 'user');
            editor.setSelection(insertAt + emoji.length + 1, 0);
        } else {
            setDescription((prev) => `${prev || ''}${emoji} `);
        }
        handleCloseEmojiPicker();
    };
    
    if (!record || eventsLoading) {
        return <DetailLoadingState title="Загружаем данные новости..." />;
    }
    
    const canPublish = record.source === 'telegram' && record.status !== 'published';
    const busy = updating || uploading;

    return (
        <Box className="news-editor-page">
            <Box component="header" className="news-editor-header">
                <Button className="news-editor-back" startIcon={<ArrowBackIcon />} onClick={() => redirect('list', 'news')}>
                    Все новости
                </Button>
                <Box className="news-editor-heading">
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography component="h1">Редактирование новости</Typography>
                        <Chip size="small" label={`#${record.id}`} />
                        <StatusChip record={record} />
                        <SourceChip source={record.source} />
                    </Stack>
                    <Typography variant="body2">
                        {title || 'Новость без названия'} · {formatNewsDate(record.event_date || record.created_at)}
                    </Typography>
                </Box>
            </Box>

            <Box className="news-editor-layout">
                <Box className="news-editor-main">
                    <Paper component="section" className="news-editor-section" elevation={0}>
                        <Box className="news-editor-section-heading">
                            <Box className="news-editor-section-icon"><ArticleOutlinedIcon /></Box>
                            <Box>
                                <Typography component="h2">Содержание</Typography>
                                <Typography variant="body2">Заголовок и текст, которые увидят посетители</Typography>
                            </Box>
                        </Box>
                        <MuiTextField
                            label="Заголовок новости"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            fullWidth
                            required
                            error={!title.trim()}
                            helperText={!title.trim() ? 'Добавьте заголовок перед сохранением' : `${title.length} символов`}
                        />
                        <Box className="news-editor-description">
                            <Box className="news-editor-field-heading">
                                <Box>
                                    <Typography component="label">Текст новости</Typography>
                                    <Typography variant="caption">Заголовки, списки, ссылки и эмодзи</Typography>
                                </Box>
                                <Button size="small" variant="outlined" startIcon={<EmojiEmotionsOutlinedIcon />} onClick={handleOpenEmojiPicker}>
                                    Эмодзи
                                </Button>
                            </Box>
                            <Popover
                                open={Boolean(emojiAnchorEl)}
                                anchorEl={emojiAnchorEl}
                                onClose={handleCloseEmojiPicker}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            >
                                <Box className="news-editor-emoji-grid">
                                    {NEWS_EMOJIS.map((emoji) => (
                                        <Button key={emoji} size="small" aria-label={`Добавить эмодзи ${emoji}`} onClick={() => handleInsertEmoji(emoji)}>
                                            {emoji}
                                        </Button>
                                    ))}
                                </Box>
                            </Popover>
                            <Box className="news-rich-editor news-editor-rich-editor">
                                <ReactQuill
                                    ref={quillRef}
                                    theme="snow"
                                    value={description}
                                    onChange={setDescription}
                                    modules={NEWS_EDITOR_MODULES}
                                    formats={NEWS_EDITOR_FORMATS}
                                    placeholder="Расскажите, что произошло и почему это интересно посетителям…"
                                />
                            </Box>
                        </Box>
                    </Paper>

                    <Paper component="section" className="news-editor-section" elevation={0}>
                        <Box className="news-editor-section-heading news-editor-section-heading-actions">
                            <Box className="news-editor-section-title">
                                <Box className="news-editor-section-icon"><PhotoLibraryOutlinedIcon /></Box>
                                <Box>
                                    <Typography component="h2">Фотографии</Typography>
                                    <Typography variant="body2">{imageUrls.length ? `${imageUrls.length} загружено` : 'Добавьте обложку и фотографии'}</Typography>
                                </Box>
                            </Box>
                            <Button variant="outlined" component="label" startIcon={<CloudUploadOutlinedIcon />} disabled={uploading}>
                                {uploading ? 'Загрузка…' : 'Добавить фото'}
                                <input type="file" hidden accept="image/*" multiple onChange={handleImageUpload} disabled={uploading} />
                            </Button>
                        </Box>
                        {imageUrls.length ? (
                            <Box className="news-editor-image-grid">
                                {imageUrls.map((imageUrl, index) => (
                                    <Box className="news-editor-image-card" key={imageUrl || index}>
                                        <Box className="news-editor-image-preview">
                                            <img src={imageUrl} alt={`Фотография ${index + 1} новости`} loading="lazy" />
                                            {index === 0 && <Chip size="small" label="Обложка" />}
                                            <Button
                                                className="news-editor-media-delete"
                                                size="small"
                                                color="error"
                                                aria-label={`Удалить фотографию ${index + 1}`}
                                                onClick={() => handleDeleteImage(index)}
                                                disabled={uploading}
                                            >
                                                <DeleteOutlineIcon />
                                            </Button>
                                        </Box>
                                        <Typography title={imageUrl} variant="caption">
                                            {index + 1}. {extractFileNameFromUrl(imageUrl) || 'Изображение'}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Box className="news-editor-media-empty">
                                <PhotoLibraryOutlinedIcon />
                                <Typography component="h3">Фотографий пока нет</Typography>
                                <Typography variant="body2">Первое изображение станет обложкой новости</Typography>
                                <Button component="label" variant="outlined" startIcon={<CloudUploadOutlinedIcon />}>
                                    Выбрать изображения
                                    <input type="file" hidden accept="image/*" multiple onChange={handleImageUpload} />
                                </Button>
                            </Box>
                        )}
                        <Typography className="news-editor-inline-note" variant="caption">
                            <InfoOutlinedIcon /> Медиа сохраняются сразу после загрузки или удаления.
                        </Typography>
                    </Paper>

                    <Paper component="section" className="news-editor-section" elevation={0}>
                        <Box className="news-editor-section-heading news-editor-section-heading-actions">
                            <Box className="news-editor-section-title">
                                <Box className="news-editor-section-icon"><PlayCircleOutlineIcon /></Box>
                                <Box>
                                    <Typography component="h2">Видео</Typography>
                                    <Typography variant="body2">{videoUrl ? 'Видео прикреплено' : 'Необязательное дополнение'}</Typography>
                                </Box>
                            </Box>
                            <Button variant="outlined" component="label" startIcon={<CloudUploadOutlinedIcon />} disabled={uploading}>
                                {videoUrl ? 'Заменить видео' : 'Добавить видео'}
                                <input type="file" hidden accept="video/*" onChange={handleVideoUpload} disabled={uploading} />
                            </Button>
                        </Box>
                        {videoUrl ? (
                            <Box className="news-editor-video-card">
                                <video src={videoUrl} controls preload="metadata" />
                                <Box>
                                    <Typography component="h3">{extractFileNameFromUrl(videoUrl) || 'Видео новости'}</Typography>
                                    <Typography variant="caption" title={videoUrl}>{videoUrl}</Typography>
                                </Box>
                                <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={handleDeleteVideo} disabled={uploading}>
                                    Удалить
                                </Button>
                            </Box>
                        ) : (
                            <Box className="news-editor-video-empty">
                                <PlayCircleOutlineIcon />
                                <Typography variant="body2">Видео не добавлено</Typography>
                            </Box>
                        )}
                    </Paper>
                </Box>

                <Box component="aside" className="news-editor-sidebar">
                    <Paper component="section" className="news-editor-side-card" elevation={0}>
                        <Box className="news-editor-side-heading">
                            <EventOutlinedIcon />
                            <Box>
                                <Typography component="h2">Публикация</Typography>
                                <Typography variant="body2">Связь с событием и статус</Typography>
                            </Box>
                        </Box>
                        <Box className="event-autocomplete news-editor-event-field">
                            <MuiTextField
                                label="Связанное событие"
                                value={eventSearchText}
                                onChange={handleEventSearchChange}
                                onFocus={() => setShowEventDropdown(true)}
                                fullWidth
                                placeholder="Найти событие…"
                                helperText={eventId !== 0 ? `Выбрано событие #${eventId}` : 'Можно оставить без события'}
                            />
                            {showEventDropdown && (
                                <Paper className="news-editor-event-menu" elevation={12}>
                                    <Typography className="news-editor-event-menu-title" variant="caption">
                                        {filteredEvents.length ? `Найдено: ${filteredEvents.length}` : 'События не найдены'}
                                    </Typography>
                                    {filteredEvents.map((event) => (
                                        <ButtonBase component="button" type="button" className="news-editor-event-option" key={event.id} onClick={() => handleEventSelect(event)}>
                                            <span>{event.name}</span><small>#{event.id}</small>
                                        </ButtonBase>
                                    ))}
                                </Paper>
                            )}
                        </Box>
                        <Box className="news-editor-status-summary">
                            <Box><Typography variant="caption">Текущий статус</Typography><StatusChip record={record} /></Box>
                            <Box><Typography variant="caption">Источник</Typography><SourceChip source={record.source} /></Box>
                        </Box>
                    </Paper>

                    <Paper component="section" className="news-editor-side-card" elevation={0}>
                        <Box className="news-editor-side-heading">
                            <InfoOutlinedIcon />
                            <Box><Typography component="h2">Сведения</Typography><Typography variant="body2">Системная информация</Typography></Box>
                        </Box>
                        <Box className="news-editor-meta-list">
                            <Box><PersonOutlineIcon /><span><small>Сотрудник</small><strong>{record.employee_id || 'Не указан'}</strong></span></Box>
                            <Box><EventOutlinedIcon /><span><small>Дата события</small><strong>{formatLocalDateTime(record.event_date) || 'Не указана'}</strong></span></Box>
                            <Box><ScheduleOutlinedIcon /><span><small>Создано</small><strong>{formatLocalDateTime(record.created_at) || 'Не указано'}</strong></span></Box>
                            <Box><ScheduleOutlinedIcon /><span><small>Обновлено</small><strong>{formatLocalDateTime(record.updated_at) || 'Не обновлялось'}</strong></span></Box>
                        </Box>
                    </Paper>

                    <Paper component="section" className="news-editor-danger-card" elevation={0}>
                        <Typography component="h2">Удаление новости</Typography>
                        <Typography variant="body2">Новость будет удалена без возможности восстановления.</Typography>
                        <Button color="error" variant="outlined" startIcon={<DeleteOutlineIcon />} onClick={() => setDeleteDialogOpen(true)} disabled={busy}>
                            Удалить новость
                        </Button>
                    </Paper>
                </Box>
            </Box>

            <Paper className="news-editor-action-bar" elevation={8}>
                <Box>
                    <Typography variant="body2">{uploading ? 'Загружаем медиа…' : 'Проверьте изменения перед сохранением'}</Typography>
                    <Typography variant="caption">Заголовок обязателен для публикации</Typography>
                </Box>
                <Stack direction="row" spacing={1.25}>
                    <Button variant="outlined" onClick={() => redirect('list', 'news')} disabled={busy}>Отмена</Button>
                    <Button variant="contained" startIcon={<SaveOutlinedIcon />} onClick={handleSave} disabled={!title.trim() || busy}>
                        {updating ? 'Сохраняем…' : 'Сохранить'}
                    </Button>
                    {canPublish && (
                        <Button variant="contained" color="success" startIcon={<CheckCircleIcon />} onClick={handlePublish} disabled={!title.trim() || busy}>
                            Сохранить и опубликовать
                        </Button>
                    )}
                </Stack>
            </Paper>

            <Dialog className="news-editor-delete-dialog" open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Удалить новость?</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">«{title || 'Без названия'}» будет удалена без возможности восстановления.</Typography>
                    <Box className="news-editor-delete-summary"><span>ID новости</span><strong>#{record.id}</strong></Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined">Отмена</Button>
                    <Button onClick={handleDelete} variant="contained" color="error" startIcon={<DeleteOutlineIcon />}>Удалить навсегда</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

// Компонент редактирования новости
export const NewsEdit = () => {
    return (
        <Edit title="Редактирование новости" actions={false} className="news-editor-resource">
            <SimpleForm
                toolbar={false}
                sx={{
                    maxWidth: 'none',
                    width: '100%',
                    '& .RaSimpleForm-form': {
                        maxWidth: 'none',
                        width: '100%',
                    },
                    '& .RaSimpleForm-stack': {
                        maxWidth: 'none',
                        width: '100%',
                        alignItems: 'stretch',
                    },
                }}
            >
                <NewsEditContent />
            </SimpleForm>
        </Edit>
    );
};
